export type FarmPortfolioDistrictId =
  | "home-acre"
  | "market-lane"
  | "north-croft"
  | "orchard-hollow"
  | "mill-pond"
  | "barn-meadow"
  | "north-ridge";

export type FarmPortfolioInvestmentId =
  | "pen-extension"
  | "first-market-garden"
  | "starter-orchard-row"
  | "garden-beds"
  | "garden-pack-shed"
  | "orchard-grove"
  | "orchard-press-house"
  | "grain-croft"
  | "gristmill"
  | "hay-meadow"
  | "hay-barn"
  | "dairy-cowshed"
  | "dairy-room"
  | "apiary"
  | "honey-house"
  | "sheepfold"
  | "fiber-shed";
export type FarmPortfolioInvestmentState = "locked" | "affordable" | "owned";
export type FarmPortfolioJobId = "lead" | "source" | "process" | "haul";
export type FarmPortfolioLineId = "poultry" | "garden" | "orchard" | "grain" | "hay" | "dairy" | "apiary" | "sheep";

export interface FarmPortfolioHudSnapshot {
  readonly cash: number;
  readonly unclaimedCash: number;
  readonly scoopCombo: number;
  readonly cargo: Readonly<{ readonly family: string; readonly count: number; readonly capacity: number }>;
  readonly districtId: FarmPortfolioDistrictId;
  readonly objectiveLabel: string;
  /** Eight Sunpatch businesses; `sold` means that line has completed at least one market sale. */
  readonly lineProgress: readonly Readonly<{
    readonly lineId: FarmPortfolioLineId;
    readonly sold: boolean;
  }>[];
  readonly estateComplete: boolean;
  /** MPH-style “you can work here” prompt when standing in a legal station. */
  readonly actionPrompt: string | null;
  readonly investments: readonly Readonly<{
    readonly id: FarmPortfolioInvestmentId;
    readonly state: FarmPortfolioInvestmentState;
    readonly price: number;
  }>[];
  readonly hand: Readonly<{
    readonly hired: boolean;
    readonly visible: boolean;
    readonly hirePrice: number;
    readonly hireAffordable: boolean;
    readonly lineId: FarmPortfolioLineId | null;
    readonly jobId: FarmPortfolioJobId | null;
    readonly pendingLineId: FarmPortfolioLineId | null;
    readonly pendingJobId: FarmPortfolioJobId | null;
    readonly assignmentAvailable: boolean;
  }>;
  readonly craft?: Readonly<{
    readonly visible: boolean;
    readonly slots: readonly Readonly<{
      readonly slotIndex: 0 | 1;
      readonly recipeId: "bakery" | "breakfast-hamper" | "dyed-gift" | null;
      readonly state: "empty" | "eligible" | "staging" | "ready";
      readonly deliveredCount: number;
      readonly requiredCount: number;
    }>[];
  }>;
  readonly context: Readonly<{
    readonly kind: "none" | "investment" | "job-board";
    readonly investmentId: FarmPortfolioInvestmentId | null;
    readonly lineId: FarmPortfolioLineId | null;
  }>;
}

export interface FarmPortfolioChoice {
  readonly kind: "investment" | "hire" | "job" | "craft-cancel";
  readonly investmentId: FarmPortfolioInvestmentId | null;
  readonly lineId: FarmPortfolioLineId | null;
  readonly jobId: FarmPortfolioJobId | null;
  readonly slotIndex?: 0 | 1;
}

export interface FarmPortfolioHudController {
  readonly root: HTMLElement;
  setSnapshot(snapshot: Readonly<FarmPortfolioHudSnapshot>): void;
  setReducedMotion(reduced: boolean): void;
  setChoiceHandler(handler: ((choice: Readonly<FarmPortfolioChoice>) => void) | null): void;
  dispose(): void;
}

const DISTRICT_LABELS: Readonly<Record<FarmPortfolioDistrictId, string>> = Object.freeze({
  "home-acre": "Home Acre",
  "market-lane": "Market Lane",
  "north-croft": "North Croft",
  "orchard-hollow": "Orchard Hollow",
  "mill-pond": "Mill Pond",
  "barn-meadow": "Barn Meadow",
  "north-ridge": "North Ridge",
});

const INVESTMENTS: Readonly<Record<FarmPortfolioInvestmentId, Readonly<{ label: string; icon: string }>>> = Object.freeze({
  "pen-extension": Object.freeze({ label: "Pen Extension", icon: "pen" }),
  "first-market-garden": Object.freeze({ label: "Market Garden", icon: "garden" }),
  "starter-orchard-row": Object.freeze({ label: "Starter Orchard", icon: "orchard" }),
  "garden-beds": Object.freeze({ label: "Expanded Garden Beds", icon: "garden" }),
  "garden-pack-shed": Object.freeze({ label: "Wash & Pack Shed", icon: "garden" }),
  "orchard-grove": Object.freeze({ label: "Full Orchard Grove", icon: "orchard" }),
  "orchard-press-house": Object.freeze({ label: "Press House", icon: "orchard" }),
  "grain-croft": Object.freeze({ label: "Grain Croft", icon: "grain" }),
  "gristmill": Object.freeze({ label: "Gristmill", icon: "mill" }),
  "hay-meadow": Object.freeze({ label: "Hay Meadow", icon: "hay" }),
  "hay-barn": Object.freeze({ label: "Hay Barn", icon: "barn" }),
  "dairy-cowshed": Object.freeze({ label: "Dairy Cowshed", icon: "cow" }),
  "dairy-room": Object.freeze({ label: "Dairy Room", icon: "cheese" }),
  apiary: Object.freeze({ label: "Apiary", icon: "honey" }),
  "honey-house": Object.freeze({ label: "Honey House", icon: "honey" }),
  sheepfold: Object.freeze({ label: "Sheepfold", icon: "sheep" }),
  "fiber-shed": Object.freeze({ label: "Fiber Shed", icon: "yarn" }),
});

const JOBS: readonly FarmPortfolioJobId[] = Object.freeze(["lead", "source", "process", "haul"]);
const LINES: readonly FarmPortfolioLineId[] = Object.freeze(["poultry", "garden", "orchard", "grain", "hay", "dairy", "apiary", "sheep"]);
const JOB_LABELS: Readonly<Record<FarmPortfolioJobId, string>> = Object.freeze({
  lead: "Lead chain",
  source: "Source",
  process: "Process",
  haul: "Haul & sell",
});

export function createFarmPortfolioHud(app: HTMLElement): FarmPortfolioHudController {
  const document = app.ownerDocument;
  const root = el(document, "section", "farm-portfolio-hud");
  root.id = "farm-portfolio-hud";
  root.setAttribute("aria-label", "Farm portfolio");
  root.dataset.district = "home-acre";
  root.dataset.reducedMotion = "false";
  root.dataset.context = "none";

  const district = el(document, "div", "farm-portfolio-hud__district");
  district.dataset.surface = "seed-tag";
  const districtGlyph = el(document, "span", "farm-portfolio-hud__district-glyph");
  districtGlyph.setAttribute("aria-hidden", "true");
  const districtName = el(document, "span", "farm-portfolio-hud__district-name");
  districtName.textContent = DISTRICT_LABELS["home-acre"];
  district.append(districtGlyph, districtName);

  const wallet = el(document, "div", "farm-portfolio-hud__wallet");
  wallet.setAttribute("aria-label", "Available cash");
  const walletGlyph = el(document, "span", "farm-portfolio-hud__wallet-glyph");
  walletGlyph.setAttribute("aria-hidden", "true");
  const walletValue = el(document, "span", "farm-portfolio-hud__wallet-value");
  walletValue.textContent = "0 Cash";
  wallet.append(walletGlyph, walletValue);

  const cargo = el(document, "div", "farm-portfolio-hud__cargo");
  cargo.setAttribute("aria-label", "Carried goods");
  const cargoGlyph = el(document, "span", "farm-portfolio-hud__cargo-glyph");
  cargoGlyph.setAttribute("aria-hidden", "true");
  const cargoValue = el(document, "span", "farm-portfolio-hud__cargo-value");
  cargoValue.textContent = "Empty · 0/8";
  cargo.append(cargoGlyph, cargoValue);

  const objective = el(document, "p", "farm-portfolio-hud__objective");
  objective.setAttribute("aria-live", "polite");
  objective.textContent = "Choose what to grow next";

  const actionPrompt = el(document, "p", "farm-portfolio-hud__action-prompt");
  actionPrompt.setAttribute("aria-live", "polite");
  actionPrompt.hidden = true;
  actionPrompt.textContent = "";

  const estate = el(document, "div", "farm-portfolio-hud__estate");
  estate.setAttribute("aria-label", "Eight-business farm progress");
  estate.dataset.complete = "false";
  const estateTitle = el(document, "span", "farm-portfolio-hud__estate-title");
  estateTitle.textContent = "0/8 businesses";
  const estateTrack = el(document, "div", "farm-portfolio-hud__estate-track");
  estateTrack.setAttribute("role", "list");
  const linePills = new Map<FarmPortfolioLineId, HTMLElement>();
  for (const line of LINES) {
    const pill = el(document, "span", "farm-portfolio-hud__line-pill");
    pill.setAttribute("role", "listitem");
    // Use estateLine (not lineId) so job-board line buttons remain the only lineId click targets.
    pill.dataset.estateLine = line;
    pill.dataset.sold = "false";
    pill.title = lineLabel(line);
    pill.textContent = lineShort(line);
    estateTrack.append(pill);
    linePills.set(line, pill);
  }
  estate.append(estateTitle, estateTrack);

  const investmentStrip = el(document, "div", "farm-portfolio-hud__investments");
  investmentStrip.setAttribute("role", "list");
  investmentStrip.setAttribute("aria-label", "Farm investments");
  const investmentButtons = new Map<FarmPortfolioInvestmentId, HTMLButtonElement>();
  for (const id of Object.keys(INVESTMENTS) as FarmPortfolioInvestmentId[]) {
    const config = INVESTMENTS[id];
    const button = el(document, "button", "farm-portfolio-hud__investment") as HTMLButtonElement;
    button.type = "button";
    button.dataset.investmentId = id;
    button.dataset.state = "locked";
    button.setAttribute("role", "listitem");
    const icon = el(document, "span", "farm-portfolio-hud__investment-icon");
    icon.dataset.icon = config.icon;
    icon.setAttribute("aria-hidden", "true");
    const copy = el(document, "span", "farm-portfolio-hud__investment-copy");
    const label = el(document, "span", "farm-portfolio-hud__investment-label");
    label.textContent = config.label;
    const price = el(document, "span", "farm-portfolio-hud__investment-price");
    price.textContent = "Locked";
    copy.append(label, price);
    button.append(icon, copy);
    investmentStrip.append(button);
    investmentButtons.set(id, button);
  }

  const handCard = el(document, "section", "farm-portfolio-hud__hand");
  handCard.hidden = true;
  handCard.setAttribute("aria-label", "Farm hand assignment");
  const handHeader = el(document, "div", "farm-portfolio-hud__hand-header");
  const handGlyph = el(document, "span", "farm-portfolio-hud__hand-glyph");
  handGlyph.setAttribute("aria-hidden", "true");
  const handStatus = el(document, "span", "farm-portfolio-hud__hand-status");
  handStatus.textContent = "No hand hired";
  handHeader.append(handGlyph, handStatus);
  const hireButton = el(document, "button", "farm-portfolio-hud__hire") as HTMLButtonElement;
  hireButton.type = "button";
  hireButton.textContent = "Hire generalist · 360 Cash";
  const lineList = el(document, "div", "farm-portfolio-hud__lines");
  lineList.setAttribute("role", "group");
  lineList.setAttribute("aria-label", "Choose production line");
  const lineButtons = new Map<FarmPortfolioLineId, HTMLButtonElement>();
  for (const line of LINES) {
    const button = el(document, "button", "farm-portfolio-hud__line") as HTMLButtonElement;
    button.type = "button";
    button.dataset.lineId = line;
    button.textContent = lineLabel(line);
    lineList.append(button);
    lineButtons.set(line, button);
  }
  const jobList = el(document, "div", "farm-portfolio-hud__jobs");
  jobList.setAttribute("role", "group");
  jobList.setAttribute("aria-label", "Assign current line job");
  const jobButtons = new Map<FarmPortfolioJobId, HTMLButtonElement>();
  for (const job of JOBS) {
    const button = el(document, "button", "farm-portfolio-hud__job") as HTMLButtonElement;
    button.type = "button";
    button.dataset.jobId = job;
    button.textContent = JOB_LABELS[job];
    jobList.append(button);
    jobButtons.set(job, button);
  }
  handCard.append(handHeader, hireButton, lineList, jobList);

  const craftCard = el(document, "section", "farm-portfolio-hud__craft");
  craftCard.hidden = true;
  craftCard.setAttribute("aria-label", "Market Lane orders");
  const craftTitle = el(document, "h2", "farm-portfolio-hud__craft-title");
  craftTitle.textContent = "Market Lane orders";
  const craftSlots: HTMLElement[] = [];
  const craftLabels: HTMLElement[] = [];
  const craftStatuses: HTMLElement[] = [];
  const craftCancelButtons: HTMLButtonElement[] = [];
  craftCard.append(craftTitle);
  for (const slotIndex of [0, 1] as const) {
    const slot = el(document, "article", "farm-portfolio-hud__craft-slot");
    slot.dataset.slotIndex = String(slotIndex);
    const label = el(document, "strong", "farm-portfolio-hud__craft-label");
    const status = el(document, "span", "farm-portfolio-hud__craft-status");
    const cancel = el(document, "button", "farm-portfolio-hud__craft-cancel") as HTMLButtonElement;
    cancel.type = "button";
    cancel.dataset.craftCancel = String(slotIndex);
    cancel.textContent = "Return goods";
    slot.append(label, status, cancel);
    craftCard.append(slot);
    craftSlots.push(slot);
    craftLabels.push(label);
    craftStatuses.push(status);
    craftCancelButtons.push(cancel);
  }

  const live = el(document, "div", "farm-portfolio-hud__live");
  live.setAttribute("role", "status");
  live.setAttribute("aria-live", "polite");
  live.setAttribute("aria-atomic", "true");
  root.append(district, wallet, cargo, objective, actionPrompt, estate, investmentStrip, craftCard, handCard, live);
  app.append(root);

  let disposed = false;
  let choiceHandler: ((choice: Readonly<FarmPortfolioChoice>) => void) | null = null;
  let currentSnapshot: Readonly<FarmPortfolioHudSnapshot> | null = null;
  let selectedLine: FarmPortfolioLineId = "poultry";
  let priorContextKind: FarmPortfolioHudSnapshot["context"]["kind"] = "none";
  let lastAnnouncement = "";
  let lastCash = -1;
  let cashBumpTimer: ReturnType<typeof setTimeout> | null = null;

  const onClick = (event: Event): void => {
    if (disposed || choiceHandler === null || currentSnapshot === null) return;
    const target = event.currentTarget as HTMLButtonElement | null;
    if (target === null || target.tagName !== "BUTTON" || target.disabled) return;
    const investmentId = target.dataset.investmentId as FarmPortfolioInvestmentId | undefined;
    if (investmentId !== undefined) {
      const visibleChoice = currentSnapshot.context.kind === "investment" && currentSnapshot.context.investmentId === investmentId;
      if (!visibleChoice) return;
      choiceHandler(Object.freeze({ kind: "investment", investmentId, lineId: null, jobId: null }));
      return;
    }
    const craftCancel = target.dataset.craftCancel;
    if (craftCancel === "0" || craftCancel === "1") {
      const slotIndex = Number(craftCancel) as 0 | 1;
      const slot = currentSnapshot.craft?.slots.find((candidate) => candidate.slotIndex === slotIndex);
      if (currentSnapshot.craft?.visible !== true || slot === undefined || (slot.state !== "staging" && slot.state !== "ready")) return;
      choiceHandler(Object.freeze({ kind: "craft-cancel", investmentId: null, lineId: null, jobId: null, slotIndex }));
      return;
    }
    const lineId = target.dataset.lineId as FarmPortfolioLineId | undefined;
    if (lineId !== undefined && currentSnapshot.context.kind === "job-board") {
      selectedLine = lineId;
      for (const [line, button] of lineButtons) button.setAttribute("aria-pressed", String(line === selectedLine));
      return;
    }
    const jobId = target.dataset.jobId as FarmPortfolioJobId | undefined;
    if (jobId !== undefined && currentSnapshot.context.kind === "job-board") {
      choiceHandler(Object.freeze({
        kind: "job",
        investmentId: null,
        lineId: selectedLine,
        jobId,
      }));
      return;
    }
    if (target === hireButton && currentSnapshot.context.kind === "job-board" && !currentSnapshot.hand.hired) {
      choiceHandler(Object.freeze({ kind: "hire", investmentId: null, lineId: null, jobId: null }));
    }
  };
  for (const button of investmentButtons.values()) button.addEventListener("click", onClick);
  hireButton.addEventListener("click", onClick);
  for (const button of lineButtons.values()) button.addEventListener("click", onClick);
  for (const button of jobButtons.values()) button.addEventListener("click", onClick);
  for (const button of craftCancelButtons) button.addEventListener("click", onClick);

  const controller: FarmPortfolioHudController = {
    root,
    setSnapshot(snapshot): void {
      if (disposed) return;
      currentSnapshot = snapshot;
      root.dataset.district = snapshot.districtId;
      root.dataset.context = snapshot.context.kind;
      districtName.textContent = DISTRICT_LABELS[snapshot.districtId];
      const cash = nonNegativeInteger(snapshot.cash);
      const unclaimed = nonNegativeInteger(snapshot.unclaimedCash ?? 0);
      const combo = nonNegativeInteger(snapshot.scoopCombo ?? 0);
      walletValue.textContent = unclaimed > 0
        ? `${cash} Cash · $${unclaimed} on ground`
        : combo > 1
          ? `${cash} Cash · x${combo} scoop!`
          : `${cash} Cash`;
      wallet.dataset.cash = String(cash);
      wallet.dataset.unclaimed = String(unclaimed);
      wallet.dataset.combo = String(combo);
      if (lastCash >= 0 && cash > lastCash) {
        wallet.dataset.cashBump = "true";
        if (cashBumpTimer !== null) clearTimeout(cashBumpTimer);
        cashBumpTimer = setTimeout(() => {
          wallet.dataset.cashBump = "false";
          cashBumpTimer = null;
        }, 400);
      }
      lastCash = cash;
      const cargoCount = nonNegativeInteger(snapshot.cargo.count);
      const cargoCapacity = nonNegativeInteger(snapshot.cargo.capacity);
      cargo.dataset.family = snapshot.cargo.family;
      cargo.dataset.count = String(cargoCount);
      cargoValue.textContent = `${cargoLabel(snapshot.cargo.family)} · ${cargoCount}/${cargoCapacity}`;
      objective.textContent = safeLabel(snapshot.objectiveLabel, "Choose what to grow next");
      const prompt = snapshot.actionPrompt?.trim() ?? "";
      actionPrompt.hidden = prompt.length === 0;
      actionPrompt.textContent = prompt;
      root.dataset.actionReady = String(prompt.length > 0);
      const progress = snapshot.lineProgress ?? [];
      const soldCount = progress.reduce((count, entry) => count + (entry.sold ? 1 : 0), 0);
      estate.dataset.complete = String(snapshot.estateComplete === true || soldCount >= LINES.length);
      estateTitle.textContent = `${soldCount}/8 businesses`;
      for (const [line, pill] of linePills) {
        const sold = progress.find((entry) => entry.lineId === line)?.sold === true;
        pill.dataset.sold = String(sold);
        pill.setAttribute("aria-label", `${lineLabel(line)} ${sold ? "sold" : "not sold yet"}`);
      }

      const byId = new Map(snapshot.investments.map((investment) => [investment.id, investment] as const));
      investmentStrip.hidden = snapshot.investments.length === 0;
      for (const [id, button] of investmentButtons) {
        const investment = byId.get(id);
        button.hidden = investment === undefined;
        const state = investment?.state ?? "locked";
        const price = nonNegativeInteger(investment?.price ?? 0);
        button.dataset.state = state;
        button.disabled = !(state === "affordable" && snapshot.context.kind === "investment" && snapshot.context.investmentId === id);
        button.setAttribute("aria-disabled", String(button.disabled));
        const priceNode = button.querySelector<HTMLElement>(".farm-portfolio-hud__investment-price");
        if (priceNode !== null) priceNode.textContent = state === "owned" ? "Owned" : state === "affordable" ? `${price} Cash` : `Locked · ${price}`;
        button.setAttribute("aria-label", `${INVESTMENTS[id].label}, ${priceNode?.textContent ?? state}`);
      }

      handCard.hidden = !(snapshot.hand.hired || snapshot.context.kind === "job-board");
      handCard.dataset.visibleWorker = String(snapshot.hand.visible);
      handCard.dataset.line = snapshot.hand.lineId ?? "none";
      handCard.dataset.job = snapshot.hand.jobId ?? "idle";
      handCard.dataset.pendingLine = snapshot.hand.pendingLineId ?? "none";
      handCard.dataset.pendingJob = snapshot.hand.pendingJobId ?? "none";
      if (snapshot.context.kind === "job-board" && priorContextKind !== "job-board") {
        selectedLine = snapshot.context.lineId ?? snapshot.hand.lineId ?? selectedLine;
      }
      handStatus.textContent = snapshot.hand.hired
        ? snapshot.hand.jobId === null || snapshot.hand.lineId === null
          ? "Farm hand · Ready for a job"
          : `Farm hand · ${lineLabel(snapshot.hand.lineId)} · ${JOB_LABELS[snapshot.hand.jobId]}`
        : "No hand hired";
      if (snapshot.hand.pendingLineId !== null && snapshot.hand.pendingJobId !== null) {
        handStatus.textContent = `Farm hand · Finishing ${lineLabel(snapshot.hand.lineId ?? snapshot.hand.pendingLineId)} · Next ${lineLabel(snapshot.hand.pendingLineId)} ${JOB_LABELS[snapshot.hand.pendingJobId]}`;
      }
      hireButton.hidden = snapshot.hand.hired;
      hireButton.disabled = !(snapshot.context.kind === "job-board" && snapshot.hand.hireAffordable);
      hireButton.textContent = snapshot.hand.hireAffordable
        ? `Hire generalist · ${nonNegativeInteger(snapshot.hand.hirePrice)} Cash`
        : `Generalist · ${nonNegativeInteger(snapshot.hand.hirePrice)} Cash`;
      jobList.hidden = !snapshot.hand.hired;
      lineList.hidden = !snapshot.hand.hired;
      for (const [line, button] of lineButtons) {
        button.disabled = snapshot.context.kind !== "job-board";
        button.setAttribute("aria-pressed", String(line === selectedLine));
      }
      for (const [job, button] of jobButtons) {
        button.disabled = !(snapshot.hand.assignmentAvailable && snapshot.context.kind === "job-board");
        button.setAttribute("aria-pressed", String(snapshot.hand.jobId === job));
      }

      const craft = snapshot.craft;
      craftCard.hidden = craft?.visible !== true;
      for (const slotIndex of [0, 1] as const) {
        const slot = craft?.slots.find((candidate) => candidate.slotIndex === slotIndex);
        const slotRoot = craftSlots[slotIndex]!;
        const cancel = craftCancelButtons[slotIndex]!;
        slotRoot.dataset.state = slot?.state ?? "empty";
        craftLabels[slotIndex]!.textContent = recipeLabel(slot?.recipeId ?? null);
        craftStatuses[slotIndex]!.textContent = slot === undefined || slot.recipeId === null
          ? "Waiting for an eligible recipe"
          : `${slot.deliveredCount}/${slot.requiredCount} ingredients · ${craftStateLabel(slot.state)}`;
        cancel.hidden = slot === undefined || (slot.state !== "staging" && slot.state !== "ready");
        cancel.disabled = craft?.visible !== true || cancel.hidden;
        cancel.setAttribute("aria-label", `Return goods from ${recipeLabel(slot?.recipeId ?? null)}`);
      }

      const craftAnnouncement = craft?.visible === true
        ? ` ${craft.slots.map((slot) => `${recipeLabel(slot.recipeId)} ${slot.deliveredCount} of ${slot.requiredCount}`).join(". ")}.`
        : "";
      const announcement = `${DISTRICT_LABELS[snapshot.districtId]}. ${objective.textContent ?? ""}.${craftAnnouncement}`;
      if (announcement !== lastAnnouncement) {
        live.textContent = announcement;
        lastAnnouncement = announcement;
      }
      priorContextKind = snapshot.context.kind;
    },
    setReducedMotion(reduced): void {
      if (disposed) return;
      root.dataset.reducedMotion = String(reduced);
    },
    setChoiceHandler(handler): void {
      if (disposed) return;
      choiceHandler = handler;
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      if (cashBumpTimer !== null) {
        clearTimeout(cashBumpTimer);
        cashBumpTimer = null;
      }
      for (const button of investmentButtons.values()) button.removeEventListener("click", onClick);
      hireButton.removeEventListener("click", onClick);
      for (const button of lineButtons.values()) button.removeEventListener("click", onClick);
      for (const button of jobButtons.values()) button.removeEventListener("click", onClick);
      for (const button of craftCancelButtons) button.removeEventListener("click", onClick);
      choiceHandler = null;
      currentSnapshot = null;
      root.remove();
    },
  };
  return Object.freeze(controller);
}

function el<K extends keyof HTMLElementTagNameMap>(document: Document, tag: K, className: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.classList.add(className);
  return node;
}

function safeLabel(value: string, fallback: string): string {
  const trimmed = value.trim();
  return trimmed.length === 0 ? fallback : trimmed.slice(0, 96);
}

function nonNegativeInteger(value: number): number {
  return Number.isSafeInteger(value) && value >= 0 ? value : 0;
}

function lineLabel(lineId: FarmPortfolioLineId): string {
  if (lineId === "poultry") return "Poultry";
  if (lineId === "garden") return "Garden";
  if (lineId === "orchard") return "Orchard";
  if (lineId === "grain") return "Grain";
  if (lineId === "hay") return "Hay";
  if (lineId === "dairy") return "Dairy";
  if (lineId === "apiary") return "Apiary";
  return "Sheep & Fiber";
}

function lineShort(lineId: FarmPortfolioLineId): string {
  if (lineId === "poultry") return "Egg";
  if (lineId === "garden") return "Veg";
  if (lineId === "orchard") return "Fruit";
  if (lineId === "grain") return "Grain";
  if (lineId === "hay") return "Hay";
  if (lineId === "dairy") return "Milk";
  if (lineId === "apiary") return "Honey";
  return "Wool";
}

function cargoLabel(family: string): string {
  if (family === "none") return "Empty";
  return family.split("-").map((part) => part.length === 0 ? part : `${part[0]!.toUpperCase()}${part.slice(1)}`).join(" ");
}

function recipeLabel(recipeId: "bakery" | "breakfast-hamper" | "dyed-gift" | null): string {
  if (recipeId === "bakery") return "Bakery parcel";
  if (recipeId === "breakfast-hamper") return "Breakfast hamper";
  if (recipeId === "dyed-gift") return "Dyed gift bundle";
  return "Empty order slot";
}

function craftStateLabel(state: "empty" | "eligible" | "staging" | "ready"): string {
  if (state === "ready") return "Ready to pack";
  if (state === "staging") return "Delivering";
  if (state === "eligible") return "Ready for ingredients";
  return "Empty";
}
